import { useMemo, useState } from 'react';

interface EpisodeListProps {
  count: number;
  current: number;
  onSelect: (n: number) => void;
  status?: string;
}

const PAGE_SIZE = 12;

export default function EpisodeList({ count, current, onSelect, status }: EpisodeListProps) {
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  const episodes = useMemo(
    () => Array.from({ length: PAGE_SIZE }, (_, i) => (page - 1) * PAGE_SIZE + i + 1).filter((n) => n <= count),
    [page, count],
  );

  return (
    <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
            <i className="ri-list-ordered"></i>
          </span>
          <h2 className="font-heading text-lg font-bold text-foreground-950">剧集列表</h2>
          <span className="text-xs text-foreground-500">共 {count} 集</span>
        </div>
        {status && (
          <span className="rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">{status}</span>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
        {episodes.map((n) => (
          <button
            key={n}
            onClick={() => onSelect(n)}
            className={`whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
              n === current
                ? 'bg-primary-500 font-medium text-background-50'
                : 'bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700'
            }`}
          >
            第{n}集
          </button>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="flex h-8 w-8 items-center justify-center rounded-md border border-background-300 text-foreground-600 disabled:opacity-40 hover:enabled:border-primary-300"
          >
            <i className="ri-arrow-left-s-line"></i>
          </button>
          <span className="text-sm text-foreground-600">
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="flex h-8 w-8 items-center justify-center rounded-md border border-background-300 text-foreground-600 disabled:opacity-40 hover:enabled:border-primary-300"
          >
            <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>
      )}
    </section>
  );
}